// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
import type { LocaleStrings } from './base';

export const skillsStrings: LocaleStrings = {
  en: {
    // ── web-ui SkillsView.vue ──
    'skills.title':                  'Skills',
    'skills.subtitle':               'Reusable instruction packs your agents can pull on for specialised tasks. Each skill bundles its own prompts, examples, and tool wiring; agents load them on demand for the right job.',
    'skills.new':                    'New skill',
    'skills.err.update':             'Failed to update skill',
    'skills.err.delete':             'Failed to delete skill',
    'skills.delete.title':           'Delete skill?',
    'skills.delete.body':            '"{name}" will be removed from the catalog. This cannot be undone.',
    'skills.empty.title':            'No skills published yet',
    'skills.empty.body':             'Your workspace is set up to run skill packs - the sandbox already mounts them at boot - but the platform catalog is empty pending the first published pack.',
    'skills.search.placeholder':     'Search skills by name, description, or category',
    'skills.search.aria':            'Search skills',
    'skills.filter.all':             'All',
    'skills.no_results.title':       'No skills match the current filters',
    'skills.no_results.clear':       'Clear filters',
    'skills.featured.title':         'Featured',
    'skills.featured.lede':          'Hand-picked packs to get your workspace started fast.',
    'skills.featured.badge':         'Featured',
    'skills.disable':                'Disable',
    'skills.enable':                 'Enable',
    'skills.system.title':           'System',
    'skills.system.lede':            'Always on. Bundled with the platform - every agent in your workspace mounts these.',
    'skills.pulls_in':               'Pulls in:',
    'skills.dep.connected':          '{name} is connected',
    'skills.dep.not_connected':      '{name} is not connected - enable it in Integrations',
    'skills.available.title':        'Available',
    'skills.available.lede':         'HQ-published packs you can enable for your workspace.',
    'skills.requires_hint':          'Requires {names} - connect it in Integrations first',
    'skills.workspace.title':        'Workspace skills',
    'skills.workspace.lede':         'Packs your workspace has authored. Private to your team and never shared with other workspaces.',
    'skills.delete_aria':            'Delete {name}',
    'skills.workspace.empty.title':  'No workspace skills yet',
    'skills.workspace.empty.body':   "Capture how your team likes to work - pipeline vocabulary, escalation rules, house style, anything you'd put in an onboarding doc. Your agents will pull on it.",
    'skills.workspace.empty.cta':    'Create your first skill',

    // ── web-ui SkillEditorView.vue ──
    'skillEditor.err.too_many_files':   'Too many files ({count}). The limit is {limit}.',
    'skillEditor.err.pack_too_big':     'Pack exceeds {kib} KiB raw.',
    'skillEditor.err.missing_skill_md': 'The folder must contain SKILL.md at the root.',
    'skillEditor.files_summary_one':    '{count} file, {kb} KB',
    'skillEditor.files_summary_other':  '{count} files, {kb} KB',
    'skillEditor.created_toast':        'Created skill "{name}"',
    'skillEditor.err.create':           'Failed to create skill',
    'skillEditor.title':                'New skill',
    'skillEditor.creating':             'Creating…',
    'skillEditor.create':               'Create skill',
    'skillEditor.details.title':        'Details',
    'skillEditor.details.lede':         'Workspace skills are private to your team - reusable instruction packs your agents pull on for the right job.',
    'skillEditor.name_placeholder':     'Pipeline stages',
    'skillEditor.optional':             'Optional',
    'skillEditor.description_placeholder': 'One short line describing when an agent should reach for this.',
    'skillEditor.content.title':        'Content',
    'skillEditor.content.lede':         'Paste a short markdown brief, or upload a folder if you want examples and reference files alongside it.',
    'skillEditor.source':               'Source',
    'skillEditor.mode_markdown':        'Paste markdown',
    'skillEditor.mode_files':           'Upload folder',
    'skillEditor.markdown.too_long':    'Too long - please trim under 64 KB.',
    'skillEditor.markdown.near_limit':  'Getting close to the 64 KB limit.',
    'skillEditor.markdown.up_to':       'Up to 64 KB',
    'skillEditor.markdown.placeholder': '# Pipeline stages\n\nWhen asked about deals, treat the stage names as ...',
    'skillEditor.folder':               'Folder',
    'skillEditor.folder_note_before':   'The folder needs',
    'skillEditor.folder_note_after':    'at the root. Up to {files} files, {kb} KB total.',
    'skillEditor.drop_aria':            'Choose folder or drop one here',
    'skillEditor.drop_strong':          'Drop a folder here',
    'skillEditor.drop_or':              'or',
    'skillEditor.drop_choose':          'choose one',

    // ── web-ui SkillDetailView.vue ──
    'skillDetail.err.load':          'Failed to load skill',
    'skillDetail.saved_toast':       'Saved "{name}"',
    'skillDetail.err.save':          'Failed to save skill',
    'skillDetail.read_only':         'Read-only',
    'skillDetail.no_content':        'This skill has no SKILL.md content.',
    'skillDetail.binary_asset':      'Binary asset (not shown)',
  },
  sv: {
    // ── web-ui SkillsView.vue ──
    'skills.title':                  'Färdigheter',
    'skills.subtitle':               'Återanvändbara instruktionspaket dina agenter kan ta hjälp av för specialiserade uppgifter. Varje färdighet samlar sina egna prompter, exempel och verktygskoppling; agenter laddar dem vid behov för rätt jobb.',
    'skills.new':                    'Ny färdighet',
    'skills.err.update':             'Det gick inte att uppdatera färdigheten',
    'skills.err.delete':             'Det gick inte att ta bort färdigheten',
    'skills.delete.title':           'Ta bort färdighet?',
    'skills.delete.body':            '"{name}" tas bort från katalogen. Detta kan inte ångras.',
    'skills.empty.title':            'Inga färdigheter publicerade än',
    'skills.empty.body':             'Din arbetsyta är inställd på att köra färdighetspaket - sandboxen monterar dem redan vid uppstart - men plattformskatalogen är tom i väntan på det första publicerade paketet.',
    'skills.search.placeholder':     'Sök färdigheter på namn, beskrivning eller kategori',
    'skills.search.aria':            'Sök färdigheter',
    'skills.filter.all':             'Alla',
    'skills.no_results.title':       'Inga färdigheter matchar de nuvarande filtren',
    'skills.no_results.clear':       'Rensa filter',
    'skills.featured.title':         'Utvalda',
    'skills.featured.lede':          'Handplockade paket för att snabbt komma igång med din arbetsyta.',
    'skills.featured.badge':         'Utvald',
    'skills.disable':                'Inaktivera',
    'skills.enable':                 'Aktivera',
    'skills.system.title':           'System',
    'skills.system.lede':            'Alltid på. Medföljer plattformen - varje agent i din arbetsyta monterar dessa.',
    'skills.pulls_in':               'Drar in:',
    'skills.dep.connected':          '{name} är ansluten',
    'skills.dep.not_connected':      '{name} är inte ansluten - aktivera den under Integrationer',
    'skills.available.title':        'Tillgängliga',
    'skills.available.lede':         'HQ-publicerade paket du kan aktivera för din arbetsyta.',
    'skills.requires_hint':          'Kräver {names} - anslut den under Integrationer först',
    'skills.workspace.title':        'Arbetsytans färdigheter',
    'skills.workspace.lede':         'Paket som din arbetsyta har skapat. Privata för ditt team och delas aldrig med andra arbetsytor.',
    'skills.delete_aria':            'Ta bort {name}',
    'skills.workspace.empty.title':  'Inga färdigheter i arbetsytan än',
    'skills.workspace.empty.body':   'Fånga hur ditt team gillar att arbeta - pipeline-vokabulär, eskaleringsregler, husstil, allt du skulle lägga i ett introduktionsdokument. Dina agenter tar hjälp av det.',
    'skills.workspace.empty.cta':    'Skapa din första färdighet',

    // ── web-ui SkillEditorView.vue ──
    'skillEditor.err.too_many_files':   'För många filer ({count}). Gränsen är {limit}.',
    'skillEditor.err.pack_too_big':     'Paketet överskrider {kib} KiB rått.',
    'skillEditor.err.missing_skill_md': 'Mappen måste innehålla SKILL.md i roten.',
    'skillEditor.files_summary_one':    '{count} fil, {kb} KB',
    'skillEditor.files_summary_other':  '{count} filer, {kb} KB',
    'skillEditor.created_toast':        'Skapade färdigheten "{name}"',
    'skillEditor.err.create':           'Det gick inte att skapa färdigheten',
    'skillEditor.title':                'Ny färdighet',
    'skillEditor.creating':             'Skapar…',
    'skillEditor.create':               'Skapa färdighet',
    'skillEditor.details.title':        'Detaljer',
    'skillEditor.details.lede':         'Arbetsytans färdigheter är privata för ditt team - återanvändbara instruktionspaket dina agenter tar hjälp av för rätt jobb.',
    'skillEditor.name_placeholder':     'Pipeline-steg',
    'skillEditor.optional':             'Valfritt',
    'skillEditor.description_placeholder': 'En kort rad som beskriver när en agent bör ta hjälp av den här.',
    'skillEditor.content.title':        'Innehåll',
    'skillEditor.content.lede':         'Klistra in en kort markdown-brief, eller ladda upp en mapp om du vill ha exempel och referensfiler vid sidan av.',
    'skillEditor.source':               'Källa',
    'skillEditor.mode_markdown':        'Klistra in markdown',
    'skillEditor.mode_files':           'Ladda upp mapp',
    'skillEditor.markdown.too_long':    'För lång - trimma den under 64 KB.',
    'skillEditor.markdown.near_limit':  'Närmar dig gränsen på 64 KB.',
    'skillEditor.markdown.up_to':       'Upp till 64 KB',
    'skillEditor.markdown.placeholder': '# Pipeline-steg\n\nNär du tillfrågas om affärer, behandla stegnamnen som ...',
    'skillEditor.folder':               'Mapp',
    'skillEditor.folder_note_before':   'Mappen behöver',
    'skillEditor.folder_note_after':    'i roten. Upp till {files} filer, {kb} KB totalt.',
    'skillEditor.drop_aria':            'Välj mapp eller släpp en här',
    'skillEditor.drop_strong':          'Släpp en mapp här',
    'skillEditor.drop_or':              'eller',
    'skillEditor.drop_choose':          'välj en',

    // ── web-ui SkillDetailView.vue ──
    'skillDetail.err.load':          'Det gick inte att läsa in färdigheten',
    'skillDetail.saved_toast':       'Sparade "{name}"',
    'skillDetail.err.save':          'Det gick inte att spara färdigheten',
    'skillDetail.read_only':         'Skrivskyddad',
    'skillDetail.no_content':        'Den här färdigheten har inget SKILL.md-innehåll.',
    'skillDetail.binary_asset':      'Binär tillgång (visas inte)',
  },
};
